export type PlainJsStoreOrmData<StoreRecord extends object, IDType extends number | string> = {
    byId: Record<IDType, StoreRecord>;
    allIds: IDType[];
};

/**
 * The PlainJsStoreORM is an equivalent to the StoreORM class, but it is intended for use with plain
 * JS objects rather than ImmutableJS objects. This is useful for slices, which do not play nicely with
 * ImmutableJS.
 */
export default class PlainJsStoreORM<StoreRecord extends object, IDType extends number | string> {
    _data: PlainJsStoreOrmData<StoreRecord, IDType>;

    constructor(data: PlainJsStoreOrmData<StoreRecord, IDType>) {
        this._data = data;
    }

    static createEmpty<StoreRecord extends object, IDType extends number | string>() {
        return new PlainJsStoreORM<StoreRecord, IDType>({
            byId: {} as Record<IDType, StoreRecord>,
            allIds: [],
        });
    }

    getByIdOrThrow(id: IDType): StoreRecord {
        const item = this._data.byId[id];
        if (!item) {
            throw new Error(`Item with id ${id} not found`);
        }
        return item;
    }

    getById(id: IDType): StoreRecord | undefined {
        return this._data.byId[id];
    }

    pushItem(item: StoreRecord, idField: keyof StoreRecord) {
        const id = item[idField];
        if (typeof id !== 'number' && typeof id !== 'string') {
            throw new Error('idField must be a number or string');
        }
        const typedId = id as IDType;
        const isNew = !this._data.allIds.includes(typedId);
        this._data = {
            ...this._data,
            byId: {
                ...this._data.byId,
                [typedId]: item,
            },
            allIds: isNew ? [...this._data.allIds, typedId] : this._data.allIds,
        };
    }

    updateById(id: IDType, updater: (item: StoreRecord) => StoreRecord) {
        const item = this.getByIdOrThrow(id);
        const updatedItem = updater(item);
        this._data = {
            ...this._data,
            byId: {
                ...this._data.byId,
                [id]: updatedItem,
            },
        };
    }

    map<T>(mapper: (item: StoreRecord) => T): T[] {
        return this._data.allIds.map((id) => mapper(this.getByIdOrThrow(id)));
    }

    get allIds(): IDType[] {
        return this._data.allIds;
    }

    get length(): number {
        return this._data.allIds.length;
    }

    get data(): PlainJsStoreOrmData<StoreRecord, IDType> {
        return this._data;
    }
}
